/**
 * Governance vocabulary shared by the Compliance report and the assistant
 * editor's Compliance and Access sections (ADR 0050, 0053).
 *
 * Moved out of AdminGovernance.vue when editing moved into the assistant
 * editor: the report and the editor must say "never approved" and "risk
 * class not set" with the same words and the same colours.
 */
import type {
  AccessClassKey,
  AccessClassSource,
  AccessModel,
  DataSource,
  ApprovalState,
  ControlStatus,
  GovernanceAssistant,
  GovernanceControl,
  RiskClass,
} from "../api/admin";

export const STATUS_LABELS: Record<ControlStatus, string> = {
  ok: "ok",
  warn: "warn",
  gap: "gap",
  na: "n/a",
};

export const FLAG_LABELS: Record<string, string> = {
  no_owner: "No owner",
  config_drift: "Config edited in UI",
  review_overdue: "Review overdue",
  unclassified: "Risk class not set",
  review_unset: "No review date",
  unapproved: "Not approved",
  changed_since_approval: "Changed since approval",
  access_unattested: "Not confirmed by the data owner",
};

export function flagLabel(f: string): string {
  return FLAG_LABELS[f] ?? f.replace(/_/g, " ");
}

const FRAMEWORK_LABELS: Record<string, string> = {
  eu_ai_act: "EU AI Act",
  iso_42001: "ISO 42001",
  gdpr: "GDPR",
  owasp_llm: "OWASP LLM",
};

/** Flatten `{eu_ai_act: ["Art. 26"], …}` into "EU AI Act Art. 26" tags. */
export function frameworkTags(c: GovernanceControl | undefined): string[] {
  const out: string[] = [];
  for (const [key, refs] of Object.entries(c?.frameworks ?? {})) {
    const label = FRAMEWORK_LABELS[key] ?? key;
    for (const ref of refs) out.push(`${label} ${ref}`);
  }
  return out;
}

export const today = new Date().toISOString().slice(0, 10);

export function isOverdue(iso: string | null): boolean {
  return iso !== null && iso < today;
}

/** ISO, deliberately: 12/15/2026 is a US date in a Danish company. */
export function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

export function fmtTs(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function fmtWhen(iso: string | null | undefined): string {
  return iso ? fmtTs(iso) : "an unrecorded date";
}

/** A StatusChip tone. */
export type Tone = "ok" | "warn" | "bad" | "info" | "neutral" | "muted";

/** Governance says ok / warn / gap / na; the chip says it in colour. */
export const STATUS_TONE: Record<ControlStatus, Tone> = {
  ok: "ok",
  warn: "warn",
  gap: "bad",
  na: "muted",
};

/** `high` demands attention; `unclassified` is a gap. */
export function riskTone(rc: RiskClass): Tone {
  if (rc === "unclassified") return "bad";
  if (rc === "high") return "warn";
  return "info";
}

/** Group-gated needs a person's attention: group membership is the whole
 * access decision. Per-person is the good state. */
export function accessTone(key: AccessClassKey | undefined): Tone {
  if (key === "group_gated" || key === "undetermined") return "warn";
  if (key === "per_person") return "ok";
  return "info";
}

export const ENFORCEMENT_LABELS: Record<
  AccessClassSource["enforcement"],
  string
> = {
  mirrored: "per person, as in the source system",
  assigned: "per person — the mapping is kept here",
  own: "the person's own data",
  group: "one group sees all of it",
  open: "public",
  undeclared: "not declared",
};

/** ADR 0066 — the AI Board's acceptance. never = not accepted (an old
 * admin approval counts as never); current = accepted what runs, within six
 * months; changed = the running configuration moved since; due = accepted
 * what runs, but more than a year ago. */
export type ApprovalKind = "never" | "current" | "changed" | "due";

export function approvalKind(a: GovernanceAssistant): ApprovalKind {
  const ap = a.approval;
  // An older backend has no `accepted`: fall back to `approved`.
  if (!(ap?.accepted ?? ap?.approved)) return "never";
  if (ap?.current) return "current";
  return ap?.matches ? "due" : "changed";
}

export const APPROVAL_TONE: Record<ApprovalKind, Tone> = {
  never: "muted",
  current: "ok",
  changed: "warn",
  due: "warn",
};

export const APPROVAL_LABELS: Record<ApprovalKind, string> = {
  never: "not yet",
  current: "accepted",
  changed: "changed",
  due: "due for review",
};

export function approverName(ap: ApprovalState | undefined): string {
  return (
    ap?.approved_by_name ||
    ap?.approved_by_email ||
    ap?.approved_by ||
    "someone"
  );
}

/** ADR 0067 — mirrored by default. How a source decides who sees what, in
 * words, and how it reads today: enforced (ok), still a DOIT AI group that
 * a data owner confirms (warn), or not yet decided (bad). */
export const ACCESS_MODEL_LABELS: Record<AccessModel, string> = {
  record: "per person",
  system: "whole system",
  open: "open",
  undecided: "to decide",
};

export function accessModelTone(
  s: Pick<DataSource, "access_model" | "mirrored">,
): Tone {
  if (!s.access_model || s.access_model === "undecided") return "bad";
  return s.mirrored ? "ok" : "warn";
}

export function accessModelText(
  s: Pick<DataSource, "access_model" | "mirrored">,
): string {
  const m = s.access_model ?? "undecided";
  if (m === "undecided" || m === "open") return ACCESS_MODEL_LABELS[m];
  return `${ACCESS_MODEL_LABELS[m]}${s.mirrored ? "" : " — planned"}`;
}
